/** Grading a fitted method's band calibration. */

import type { SentinelRunMethodResult } from 'features/sentinelDesign/types'

/** Nominal in-band fraction a ±band is built to hit when nothing says otherwise. */
export const NOMINAL_BAND_COVERAGE = 0.95

/**
 * How far measured coverage may fall below nominal before the method is called
 * miscalibrated. Generous on purpose: the known failure (52-86% exceedance
 * against a nominal 5%) is nowhere near this line.
 */
const COVERAGE_TOLERANCE = 0.05

export type BandCoverageLevel = 'ok' | 'low' | 'unknown'

export interface BandCoverageVerdict {
  level: BandCoverageLevel
  /** Measured in-band fraction on held-out rows, in [0, 1]. */
  measured: number | null
  /** Fraction the band was built to hit, in [0, 1]. */
  expected: number
  /** True when the backend itself flagged the method rather than us deriving it. */
  flaggedByBackend: boolean
  note: string | null
}

/**
 * Grade one method's band calibration. A backend flag is authoritative; with
 * no flag, held-out (test, else val) coverage is compared against nominal so a
 * method whose band plainly does not hold cannot present as trustworthy as the
 * rest.
 */
export const bandCoverageVerdict = (
  method: SentinelRunMethodResult | undefined
): BandCoverageVerdict => {
  const expected = method?.band_coverage_expected ?? NOMINAL_BAND_COVERAGE
  const note = method?.band_coverage_note ?? null
  const measured =
    method?.metrics_by_role?.test?.band_coverage ??
    method?.metrics_by_role?.val?.band_coverage ??
    null

  if (method?.band_coverage_ok === false) {
    return { level: 'low', measured, expected, flaggedByBackend: true, note }
  }
  if (method?.band_coverage_ok === true) {
    return { level: 'ok', measured, expected, flaggedByBackend: true, note }
  }
  if (measured === null || !Number.isFinite(measured)) {
    return {
      level: 'unknown',
      measured: null,
      expected,
      flaggedByBackend: false,
      note,
    }
  }
  return {
    level: measured < expected - COVERAGE_TOLERANCE ? 'low' : 'ok',
    measured,
    expected,
    flaggedByBackend: false,
    note,
  }
}
